import { useRef, useState } from "react";
import { toast } from "sonner";
import { Images, Star, Trash2, RefreshCw, Upload, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { btnPrimary } from "./ui";

const MAX = 8 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

function validate(files) {
  const bad = files.find((f) => !TYPES.includes(f.type) || f.size > MAX);
  if (bad) { toast.error(`${bad.name}: use JPG, PNG ou WEBP até 8 MB`); return false; }
  return true;
}

export default function PhotoManager({ product, onChanged }) {
  const [images, setImages] = useState(product.images || []);
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const addRef = useRef();
  const replaceRef = useRef();
  const [replacing, setReplacing] = useState(null);
  const base = `/store/products/${product.id}/images`;

  const run = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setImages(data.images || []); onChanged(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };
  const pick = (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length || !validate(files)) return;
    setPreviews(files.map((f) => ({ file: f, url: URL.createObjectURL(f) })));
  };
  const upload = async () => {
    const form = new FormData();
    previews.forEach((p) => form.append("files", p.file));
    if (await run(() => api.post(base, form))) { toast.success("Fotografias carregadas"); previews.forEach((p) => URL.revokeObjectURL(p.url)); setPreviews([]); }
  };
  const replace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validate([file])) return;
    const form = new FormData();
    form.append("file", file);
    if (await run(() => api.put(`${base}/${replacing}`, form))) toast.success("Fotografia substituída");
  };
  return (
    <Dialog>
      <DialogTrigger className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium hover:border-bo-green" data-testid={`photos-btn-${product.id}`}>
        <Images className="h-3.5 w-3.5" />Fotos ({images.length})
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Fotografias · {product.name}</DialogTitle></DialogHeader>
        <input ref={addRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="photos-file-input" />
        <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={replace} data-testid="photos-replace-input" />
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="photos-grid">
          {images.map((img) => (
            <div key={img.id} className={`group relative overflow-hidden rounded-xl border-2 ${img.main ? "border-bo-green" : "border-transparent"}`} data-testid={`photo-${img.id}`}>
              <img src={img.url} alt="" className="aspect-square w-full object-cover" />
              {img.main && <span className="absolute left-1 top-1 rounded-full bg-bo-green px-2 py-0.5 text-[10px] font-bold text-white">Principal</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/50 p-1">
                {!img.main && <button disabled={busy} title="Definir principal" onClick={() => run(() => api.post(`${base}/${img.id}/main`))} className="rounded p-1 text-white hover:bg-white/20" data-testid={`photo-main-${img.id}`}><Star className="h-4 w-4" /></button>}
                <button disabled={busy} title="Substituir" onClick={() => { setReplacing(img.id); replaceRef.current.click(); }} className="rounded p-1 text-white hover:bg-white/20" data-testid={`photo-replace-${img.id}`}><RefreshCw className="h-4 w-4" /></button>
                <button disabled={busy} title="Remover" onClick={() => window.confirm("Remover fotografia?") && run(() => api.delete(`${base}/${img.id}`))} className="rounded p-1 text-white hover:bg-white/20" data-testid={`photo-delete-${img.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {previews.map((p) => <img key={p.url} src={p.url} alt="" className="aspect-square w-full rounded-xl border-2 border-dashed border-bo-yellow object-cover opacity-80" data-testid="photo-preview" />)}
        </div>
        {!images.length && !previews.length && <p className="text-sm text-gray-500">Sem fotografias. Até 8 por produto.</p>}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => addRef.current.click()} disabled={busy || images.length >= 8} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm" data-testid="photos-pick-btn"><Images className="h-4 w-4" />Escolher fotografias</button>
          {previews.length > 0 && <button onClick={upload} disabled={busy} className={btnPrimary} data-testid="photos-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Carregar {previews.length}</button>}
        </div>
        <p className="text-xs text-gray-500">JPG, PNG ou WEBP até 8 MB. As imagens são otimizadas (WEBP, máx. 1600 px).</p>
      </DialogContent>
    </Dialog>
  );
}
